import type { Rating } from "../types/rating";
import type { DateValue } from "../types/data";
import { isUnknownArray, isTimestamp } from "../types/data";
import type { StreakPenalty, StreakRank, StreakUpdateOptions, WeekQualityAward, WeeklyStreakAward, WeeklyStreakState } from "../types/streak";
import {
  deduplicateRatingsByMediaId,
  hasImportMarker,
  isBackfilledManualRating,
  isCurrentProgressRating,
  isGameProgressEligible,
  isShortFilm
} from "./gameProgressEligibility";
import {
  getCachedOrFetch,
  invalidateCachedValue
} from "./localDataCache";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import {
  getWeeklyStreakExperienceComponentId,
  getWeeklyStreakLedgerExperience,
  WEEKLY_STREAK_EVENT_START_WEEK
} from "./weeklyStreakEvents";

export const STREAK_RANKS: StreakRank[] = [
  { key: "iron", name: "Железная", icon: "⛓️", weeklyFilms: 1, bonus: 10 },
  { key: "bronze", name: "Бронзовая", icon: "🥉", weeklyFilms: 3, bonus: 30 },
  { key: "silver", name: "Серебряная", icon: "🥈", weeklyFilms: 5, bonus: 50 },
  { key: "gold", name: "Золотая", icon: "🥇", weeklyFilms: 7, bonus: 80 },
  { key: "platinum", name: "Платиновая", icon: "🏆", weeklyFilms: 14, bonus: 200 },
  { key: "diamond", name: "Алмазная", icon: "💎", weeklyFilms: 21, bonus: 330 },
  { key: "legendary", name: "Легендарная", icon: "👑", weeklyFilms: 28, bonus: 450 }
];

export const WEEK_QUALITY_RANKS = STREAK_RANKS;

const DAY = 24 * 60 * 60 * 1000;
const WEEK = 7 * DAY;
const STREAK_STATE_CACHE_TTL = 10 * 60 * 1000;
export const WEEKLY_STREAK_REPLAY_START_WEEK = WEEKLY_STREAK_EVENT_START_WEEK;
const WEEKLY_STREAK_REPLAY_VERSION = 1;
// Version 1 stored a calculated view of historical weeks. It could change
// when a rating was edited, so it cannot be treated as an award ledger.
// Version 3 stores a closed-week snapshot. It deliberately does not scan the
// activity journal: the journal recovery was expensive and could alter a
// profile's XP long after the original action. A rating mutation may refresh
// only its own first-rating week when the source data is corrected.
const WEEK_QUALITY_SNAPSHOT_VERSION = 3;

const toDate = (value: DateValue | undefined) => {
  if (!value) return null;
  const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
    typeof value === "string" || typeof value === "number" ? new Date(value) : new Date(NaN);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toRawRatingDate = (rating: Rating) => {
  const value = rating?.ratedAt || rating?.lastRatedAt || rating?.firstRatedAt || rating?.firstTrackedAt;
  return toDate(value);
};

const getActiveUniqueRatings = (ratings: Rating[] = []) =>
  deduplicateRatingsByMediaId(
    (isUnknownArray(ratings) ? ratings : [])
      .filter((rating: Rating) => rating && rating.active !== false)
  );

const getRatingDayCounts = (ratings: Rating[] = []) => {
  const counts = new Map<string, number>();
  getActiveUniqueRatings(ratings).forEach((rating: Rating) => {
    if (!rating) return;
    if (isShortFilm(rating) || !isCurrentProgressRating(rating)) return;
    const date = toRawRatingDate(rating);
    if (!date) return;
    const dayKey = formatDateKey(date);
    counts.set(dayKey, (counts.get(dayKey) || 0) + 1);
  });
  return counts;
};

const isTimelineProgressEligible = (rating: Rating, dayCounts: Map<string, number> | null = null) => {
  if (!rating) return false;
  if (isShortFilm(rating) || !isCurrentProgressRating(rating)) {
    return false;
  }

  if (rating?.streakEligible !== false) return true;

  const date = toRawRatingDate(rating);
  const dayCount = date && dayCounts ? dayCounts.get(formatDateKey(date)) || 0 : 0;
  return dayCount > 0 && dayCount < 7;
};

const formatDateKey = (date = new Date()) => {
  const normalizedDate = new Date(date);
  return [
    normalizedDate.getFullYear(),
    String(normalizedDate.getMonth() + 1).padStart(2, "0"),
    String(normalizedDate.getDate()).padStart(2, "0")
  ].join("-");
};

const parseDateKey = (key: string | number | Date | null | undefined) => {
  if (key instanceof Date) return new Date(key);
  const [year, month, day] = String(key || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const getMonthKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export const getWeekKey = (date = new Date()) => formatDateKey(getWeekStart(date));

export const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const weekStart = new Date(d.setDate(diff));
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
};

const parseWeekKey = (key: string | null | undefined) => {
  const parsedDate = parseDateKey(key);
  if (!parsedDate) return null;

  // Старые ключи строились через toISOString() и в положительных таймзонах
  // могли сохранять понедельник как воскресенье.
  if (parsedDate.getDay() === 0) {
    const migratedDate = new Date(parsedDate);
    migratedDate.setDate(migratedDate.getDate() + 1);
    return getWeekStart(migratedDate);
  }

  return getWeekStart(parsedDate);
};

const normalizeWeekKey = (weekKey: string | null | undefined) => {
  const weekStart = parseWeekKey(weekKey);
  return weekStart ? getWeekKey(weekStart) : null;
};

// New rating documents keep this key fixed at the first eligible rating.
// Falling back to the mutable rating date is only for legacy documents that
// predate the field. Callers can use the same helper while constructing a new
// rating document so a later edit cannot move earned streak XP between weeks.
export const getRatingStreakWeekKey = (rating: Rating) => {
  const storedWeekKey = normalizeWeekKey(rating?.streakWeekKey);
  if (storedWeekKey) return storedWeekKey;
  const ratingDate = toRawRatingDate(rating);
  return ratingDate ? getWeekKey(ratingDate) : null;
};

const normalizeWeekKeyMap = <T>(value: Record<string, T> = {}): Record<string, T> =>
  Object.fromEntries(
    Object.entries(value || {}).map(([weekKey, weekValue]) => {
      return [normalizeWeekKey(weekKey) || weekKey, weekValue];
    })
  );

const toNonNegativeNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
};

const toNonNegativeInteger = (value: unknown) =>
  Math.floor(toNonNegativeNumber(value));

const normalizeAwardedWeeks = (value: Record<string, WeeklyStreakAward> = {}): Record<string, WeeklyStreakAward> => Object.fromEntries(
  Object.entries(value || {})
    .map(([rawWeekKey, rawAward]): [string, WeeklyStreakAward] | null => {
      const weekKey = normalizeWeekKey(rawWeekKey);
      const bonus = toNonNegativeNumber(rawAward?.bonus);
      const streakWeekNumber = toNonNegativeInteger(rawAward?.streakWeekNumber);
      if (!weekKey || bonus <= 0 || streakWeekNumber <= 0) return null;
      return [weekKey, {
        ...rawAward,
        componentId: getWeeklyStreakExperienceComponentId(weekKey),
        bonus,
        streakWeekNumber
      }];
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
);

export const getFilmsThisWeek = (ratings: Rating[]) => getFilmsForWeek(ratings, getWeekStart());

export const getFilmsForWeek = (ratings: Rating[] | undefined, weekStartDate: Date | undefined) => {
  const weekKey = getWeekKey(weekStartDate);
  const sourceRatings = getActiveUniqueRatings(ratings);
  const dayCounts = getRatingDayCounts(sourceRatings);

  return sourceRatings.filter((rating: Rating) => {
    if (!isTimelineProgressEligible(rating, dayCounts)) return false;
    return getRatingStreakWeekKey(rating) === weekKey;
  }).length;
};

const getRatingsByWeek = (ratings: Rating[] = []) => {
  const weeks = new Map<string, number>();
  const sourceRatings = getActiveUniqueRatings(ratings);
  const dayCounts = getRatingDayCounts(sourceRatings);
  sourceRatings.forEach((rating: Rating) => {
    if (!isTimelineProgressEligible(rating, dayCounts)) return;
    const key = getRatingStreakWeekKey(rating);
    if (!key) return;
    weeks.set(key, (weeks.get(key) || 0) + 1);
  });
  return weeks;
};

const getCurrentWeeklyStreakWeekKeysFromRatings = (
  ratings: Rating[] = [],
  referenceDate = new Date()
) => {
  const weeks = getRatingsByWeek(ratings);
  if (weeks.size === 0) return [];

  let weekStart = getWeekStart(referenceDate);
  let weekKey = getWeekKey(weekStart);
  if ((weeks.get(weekKey) || 0) < 1) {
    weekStart = new Date(weekStart.getTime() - WEEK);
    weekKey = getWeekKey(weekStart);
  }

  const weekKeys: string[] = [];
  while ((weeks.get(weekKey) || 0) >= 1) {
    weekKeys.push(weekKey);
    weekStart = new Date(weekStart.getTime() - WEEK);
    weekKey = getWeekKey(weekStart);
  }

  return weekKeys;
};

const getStoredQualityWeekKey = (rating: Rating) => {
  const savedWeekKey = normalizeWeekKey(rating?.weekQualityWeekKey);
  if (savedWeekKey) return savedWeekKey;

  const firstRatingDate = toDate(
    rating?.firstRatedAt ||
    rating?.ratedAt ||
    rating?.firstTrackedAt
  );
  return firstRatingDate ? getWeekKey(firstRatingDate) : null;
};

const hasMismatchedFirstTrackingWeek = (rating: Rating) => {
  const firstTrackedDate = toDate(rating?.firstTrackedAt);
  if (!firstTrackedDate) {
    return isBackfilledManualRating(rating);
  }

  const trackedWeekKey = getWeekKey(firstTrackedDate);
  const storedWeekKey = normalizeWeekKey(rating?.weekQualityWeekKey);
  const firstRatedDate = toDate(rating?.firstRatedAt || rating?.ratedAt);
  const firstRatedWeekKey = firstRatedDate ? getWeekKey(firstRatedDate) : null;

  return Boolean(
    (storedWeekKey && storedWeekKey !== trackedWeekKey) ||
    (firstRatedWeekKey && firstRatedWeekKey !== trackedWeekKey)
  );
};

const isWeekQualityEligible = (rating: Rating, dayCounts: Map<string, number> | null = null) => {
  if (!rating) return false;
  if (hasImportMarker(rating)) return false;
  // `firstTrackedAt` is the immutable moment when the app first received the
  // rating. A manually entered older watched date must not manufacture a
  // quality award for that historical week, even if an older client saved an
  // explicit `weekQualityEligibleAtFirstRating: true` flag.
  if (hasMismatchedFirstTrackingWeek(rating)) return false;
  const canRecoverSameWeekManualRating = Boolean(
    toDate(rating.firstTrackedAt) &&
    (rating.firstProgressMode || rating.progressMode) === "archive" &&
    isTimelineProgressEligible(rating, dayCounts)
  );
  if (typeof rating.weekQualityEligibleAtFirstRating === "boolean") {
    return rating.weekQualityEligibleAtFirstRating || canRecoverSameWeekManualRating;
  }
  return isGameProgressEligible(rating) || canRecoverSameWeekManualRating;
};

const getWeekQualityWeekKey = (rating: Rating) => {
  return getStoredQualityWeekKey(rating);
};

const getQualityRatingsByWeek = (ratings: Rating[] = []) => {
  const weeks = new Map<string, number>();
  const sourceRatings = getActiveUniqueRatings(ratings);
  const dayCounts = getRatingDayCounts(sourceRatings);
  sourceRatings.forEach((rating: Rating) => {
    if (!isWeekQualityEligible(rating, dayCounts)) return;
    const key = getWeekQualityWeekKey(rating);
    if (!key) return;
    weeks.set(key, (weeks.get(key) || 0) + 1);
  });
  return weeks;
};

const getInvalidStoredQualityWeeks = (ratings: Rating[] = []) =>
  [...new Set(
    getActiveUniqueRatings(ratings)
      .filter((rating: Rating) =>
        hasMismatchedFirstTrackingWeek(rating) ||
        (
          rating?.weekQualityEligibleAtFirstRating === true &&
          !isWeekQualityEligible(rating)
        )
      )
      .map(getStoredQualityWeekKey)
      .filter((weekKey): weekKey is string => Boolean(weekKey))
  )];

export const getQualityRankForFilms = (weeklyFilms = 0) =>
  [...WEEK_QUALITY_RANKS].reverse().find((rank) => weeklyFilms >= rank.weeklyFilms) || null;

export const getRankForActivity = getQualityRankForFilms;

export const calculateStreakWeekBonus = (weekNumber: number) => {
  const normalizedWeek = Math.max(0, Number(weekNumber) || 0);
  return normalizedWeek > 0 ? 10 + normalizedWeek : 0;
};

export const calculateStreakExperience = (streakCount: number) => {
  const count = Math.max(0, Number(streakCount) || 0);
  return count * 10 + (count * (count + 1)) / 2;
};

export const calculateWeekQualityStats = (ratings: Rating[] = [], options: { includeCurrentWeek?: boolean; referenceDate?: Date } = {}) => {
  const {
    includeCurrentWeek = false,
    referenceDate = new Date()
  } = options;
  const weeks = getQualityRatingsByWeek(ratings);
  const currentWeekStart = getWeekStart(referenceDate);
  const qualityCounts = Object.fromEntries(WEEK_QUALITY_RANKS.map((rank) => [rank.key, 0]));
  let qualityBonusTotal = 0;
  const weekQualities: Record<string, WeekQualityAward> = {};

  weeks.forEach((count, weekKey) => {
    const weekStart = parseDateKey(weekKey);
    if (!includeCurrentWeek && weekStart && weekStart >= currentWeekStart) return;
    const rank = getQualityRankForFilms(count);
    if (!rank) return;
    qualityCounts[rank.key] += 1;
    qualityBonusTotal += rank.bonus;
    weekQualities[weekKey] = {
      rank: rank.key,
      rankName: rank.name,
      films: count,
      bonus: rank.bonus
    };
  });

  return {
    qualityCounts,
    qualityBonusTotal,
    weekQualities,
    totalQualifiedWeeks: Object.values(qualityCounts).reduce((sum, value) => sum + value, 0)
  };
};

export const calculateCurrentWeeklyStreakFromRatings = (ratings: Rating[] = [], referenceDate = new Date()) => {
  return getCurrentWeeklyStreakWeekKeysFromRatings(ratings, referenceDate).length;
};

export const calculateQualifiedStreakFromRatings = calculateCurrentWeeklyStreakFromRatings;

export const checkWeeklyStreakCompletion = (ratings: Rating[], weekStartDate: Date | undefined) =>
  getFilmsForWeek(ratings, weekStartDate) >= 1;

const defaultStreakData: WeeklyStreakState = {
  count: 0,
  bestCount: 0,
  bonusTotal: 0,
  streakBonusTotal: 0,
  qualityBonusTotal: 0,
  lastUpdatedWeek: null,
  awardedWeeks: {},
  streakReplayVersion: 0,
  streakReplayBoundaryWeek: null,
  streakReplayAnchor: null,
  legacyStreakBonusTotal: 0,
  protectedMonths: {},
  qualityCounts: Object.fromEntries(WEEK_QUALITY_RANKS.map((rank) => [rank.key, 0])),
  weekQualities: {},
  // Closed weeks are a reward ledger. Once a week has ended, its result must
  // never be rebuilt from later edits to a rating.
  finalizedWeekQualities: {},
  weekQualitySnapshotVersion: WEEK_QUALITY_SNAPSHOT_VERSION,
  weekQualityJournalRecoveredAt: null
};

const normalizeStreakData = (data: Partial<WeeklyStreakState> = {}): WeeklyStreakState => {
  const rawCount = Number(data.count);
  const count = Number.isFinite(rawCount) ? Math.max(0, rawCount) : 0;
  const rawBestCount = Number(data.bestCount);
  const bestCount = Math.max(
    Number.isFinite(rawBestCount) ? Math.max(0, rawBestCount) : count,
    count
  );
  const awardedWeeks = normalizeAwardedWeeks(data.awardedWeeks);
  const hasStoredStreakBonusTotal = Object.prototype.hasOwnProperty.call(
    data || {},
    "streakBonusTotal"
  );
  const legacyAwardedTotal = Object.values(awardedWeeks).reduce(
    (total, award) => Math.min(
      Number.MAX_SAFE_INTEGER,
      total + toNonNegativeNumber(award?.bonus)
    ),
    0
  );
  const rawReplayVersion = Number(data.streakReplayVersion);
  const streakReplayVersion = Number.isFinite(rawReplayVersion)
    ? Math.max(0, Math.floor(rawReplayVersion))
    : 0;
  const rawReplayAnchor = data.streakReplayAnchor;
  const streakReplayAnchor = rawReplayAnchor && typeof rawReplayAnchor === "object"
    ? {
      count: toNonNegativeInteger(rawReplayAnchor.count),
      bestCount: Math.max(
        toNonNegativeInteger(rawReplayAnchor.bestCount),
        toNonNegativeInteger(rawReplayAnchor.count)
      ),
      protectedMonths: { ...(rawReplayAnchor.protectedMonths || {}) }
    }
    : null;
  const savedSnapshotVersion = Number(data.weekQualitySnapshotVersion || 0);
  const hasLegacyQualityData = Boolean(
    Object.keys(data.finalizedWeekQualities || {}).length ||
    Object.keys(data.weekQualities || {}).length
  );
  const sourceSnapshotVersion = savedSnapshotVersion || (hasLegacyQualityData ? 1 : WEEK_QUALITY_SNAPSHOT_VERSION);
  // Version 2 already stored snapshots, but an edit could have replaced one
  // with a lower rank. Keep it as a fallback and let the activity journal
  // restore the higher immutable award during the v3 migration.
  const finalizedWeekQualities = sourceSnapshotVersion >= 2
    ? normalizeWeekKeyMap(data.finalizedWeekQualities || data.weekQualities)
    : {};

  const normalized: WeeklyStreakState = {
    ...defaultStreakData,
    ...data,
    count,
    bestCount,
    bonusTotal: toNonNegativeNumber(data.bonusTotal),
    streakBonusTotal: hasStoredStreakBonusTotal
      ? toNonNegativeNumber(data.streakBonusTotal)
      : legacyAwardedTotal,
    qualityBonusTotal: toNonNegativeNumber(data.qualityBonusTotal),
    lastUpdatedWeek: normalizeWeekKey(data.lastUpdatedWeek),
    awardedWeeks,
    streakReplayVersion,
    streakReplayBoundaryWeek: normalizeWeekKey(data.streakReplayBoundaryWeek),
    streakReplayAnchor,
    legacyStreakBonusTotal: toNonNegativeNumber(data.legacyStreakBonusTotal),
    protectedMonths: data.protectedMonths || {},
    qualityCounts: {
      ...defaultStreakData.qualityCounts,
      ...(data.qualityCounts || {})
    },
    weekQualities: finalizedWeekQualities,
    finalizedWeekQualities,
    weekQualitySnapshotVersion: sourceSnapshotVersion,
    weekQualityJournalRecoveredAt: data.weekQualityJournalRecoveredAt || null
  };

  // A versioned replay ledger is self-describing. Legacy documents retain
  // their exact stored aggregate until a one-time migration creates the safe
  // boundary anchor; bestCount is never used to manufacture missing XP.
  normalized.streakBonusTotal = getWeeklyStreakLedgerExperience(normalized);
  normalized.bonusTotal = normalized.streakBonusTotal + normalized.qualityBonusTotal;
  return normalized;
};

const createWeekQualitySnapshot = (films: number | undefined): WeekQualityAward | null => {
  const rank = getQualityRankForFilms(films);
  if (!rank) return null;
  return {
    rank: rank.key,
    rankName: rank.name,
    films,
    bonus: rank.bonus
  };
};

const getSnapshotQualityStats = (weekQualities: Record<string, WeekQualityAward> = {}) => {
  const qualityCounts = Object.fromEntries(WEEK_QUALITY_RANKS.map((rank) => [rank.key, 0]));
  let qualityBonusTotal = 0;

  Object.values(weekQualities || {}).forEach((quality) => {
    const rank = WEEK_QUALITY_RANKS.find((item) => item.key === quality?.rank);
    if (!rank) return;
    qualityCounts[rank.key] += 1;
    qualityBonusTotal += Number(quality?.bonus) || rank.bonus;
  });

  return {
    qualityCounts,
    qualityBonusTotal,
    totalQualifiedWeeks: Object.values(qualityCounts).reduce((sum, value) => sum + value, 0)
  };
};

const finalizeClosedWeekQualities = (
  storedQualities: Record<string, WeekQualityAward>,
  ratings: Rating[],
  currentWeekStart: Date,
  recalculateQualityWeeks: string[] = [],
  allowedQualityWeeks: string[] | null = null
) => {
  const normalizedAllowedWeeks = isUnknownArray(allowedQualityWeeks)
    ? new Set(allowedQualityWeeks.map(normalizeWeekKey).filter(Boolean))
    : null;
  const snapshots = Object.fromEntries(
    Object.entries(storedQualities || {}).filter(([weekKey]) => {
      const normalizedWeekKey = normalizeWeekKey(weekKey);
      const weekStart = parseWeekKey(normalizedWeekKey);

      // Older clients could persist the current rank as if it had already
      // been earned. An open (or future) week is only a preview: keeping that
      // snapshot would immediately add +10/+30/+50/... XP while the user is
      // still watching films during the week.
      if (!weekStart || weekStart >= currentWeekStart) return false;

      return (
        (
          !normalizedAllowedWeeks ||
          normalizedAllowedWeeks.has(normalizedWeekKey)
        ) &&
        WEEK_QUALITY_RANKS.some((rank) =>
          rank.key === storedQualities?.[weekKey]?.rank
        )
      );
    })
  );
  const qualityRatingsByWeek = getQualityRatingsByWeek(ratings);

  // A normal read keeps an earned closed-week snapshot intact. A rating
  // mutation, however, supplies the first-rating week it changed. Rebuild
  // only that snapshot so an old manually backdated rating can no longer
  // leave a phantom iron/bronze/etc. week in the results.
  [...new Set(recalculateQualityWeeks.map(normalizeWeekKey).filter((weekKey): weekKey is string => Boolean(weekKey)))]
    .forEach((weekKey) => {
      const weekStart = parseWeekKey(weekKey);
      if (!weekStart || weekStart >= currentWeekStart) return;

      const snapshot = createWeekQualitySnapshot(qualityRatingsByWeek.get(weekKey) || 0);
      if (snapshot) snapshots[weekKey] = snapshot;
      else delete snapshots[weekKey];
    });

  qualityRatingsByWeek.forEach((films, weekKey) => {
    if (normalizedAllowedWeeks && !normalizedAllowedWeeks.has(weekKey)) return;
    const weekStart = parseWeekKey(weekKey);
    if (!weekStart || weekStart >= currentWeekStart || snapshots[weekKey]) return;
    const snapshot = createWeekQualitySnapshot(films);
    if (snapshot) snapshots[weekKey] = snapshot;
  });

  return normalizeWeekKeyMap(snapshots);
};

const streakDocRef = (firestore: firebase.firestore.Firestore, userId: string) =>
  firestore.collection("users").doc(userId).collection("weeklyStreak").doc("state");

const loadStreakDataFromServer = async (userId: string, firestore: firebase.firestore.Firestore | null | undefined) => {
  if (!userId || !firestore) return { ...defaultStreakData };
  const snapshot = await streakDocRef(firestore, userId).get();
  return normalizeStreakData(snapshot.exists ? snapshot.data() : {});
};

const loadStreakData = async (userId: string, firestore: firebase.firestore.Firestore | null | undefined, { cache = false }: { cache?: boolean } = {}) => {
  if (!cache) return loadStreakDataFromServer(userId, firestore);
  const cachedData = await getCachedOrFetch(
    `weekly_streak_state_v1_${userId}`,
    () => loadStreakDataFromServer(userId, firestore),
    { ttl: STREAK_STATE_CACHE_TTL }
  );
  // Cached state can predate the durable weekly-quality snapshot migration.
  return normalizeStreakData(cachedData);
};

const saveStreakData = async (
  userId: string,
  firestore: firebase.firestore.Firestore | null | undefined,
  streakData: WeeklyStreakState,
  {
    removedQualityWeeks = [],
    removedStreakWeeks = [],
    removedProtectionMonths = []
  }: { removedQualityWeeks?: string[]; removedStreakWeeks?: string[]; removedProtectionMonths?: string[] } = {}
) => {
  if (!userId || !firestore) return;
  const stateRef = streakDocRef(firestore, userId);
  await stateRef.set({
    ...streakData,
    updatedAt: new Date()
  }, { merge: true });

  // Firestore merges maps recursively, so omitting a key from a refreshed
  // target map does not remove it. A failed cleanup must reject: otherwise an
  // XP caller could settle against state that was only partially persisted.
  const normalizedRemovedWeeks = [...new Set(
    (isUnknownArray(removedQualityWeeks) ? removedQualityWeeks : [])
      .map(normalizeWeekKey)
      .filter(Boolean)
  )];
  const fieldsToDelete: Record<string, firebase.firestore.FieldValue> = {};
  normalizedRemovedWeeks.forEach((weekKey) => {
    fieldsToDelete[`finalizedWeekQualities.${weekKey}`] = firebase.firestore.FieldValue.delete();
    fieldsToDelete[`weekQualities.${weekKey}`] = firebase.firestore.FieldValue.delete();
  });
  const normalizedRemovedStreakWeeks = [...new Set(
    (isUnknownArray(removedStreakWeeks) ? removedStreakWeeks : [])
      .map(normalizeWeekKey)
      .filter(Boolean)
  )];
  normalizedRemovedStreakWeeks.forEach((weekKey) => {
    fieldsToDelete[`awardedWeeks.${weekKey}`] =
      firebase.firestore.FieldValue.delete();
  });
  [...new Set(
    (isUnknownArray(removedProtectionMonths) ? removedProtectionMonths : [])
      .filter((monthKey) => /^\d{4}-\d{2}$/.test(String(monthKey || "")))
  )].forEach((monthKey) => {
    fieldsToDelete[`protectedMonths.${monthKey}`] =
      firebase.firestore.FieldValue.delete();
  });
  if (Object.keys(fieldsToDelete).length > 0) {
    await stateRef.update(fieldsToDelete);
  }
  invalidateCachedValue(`weekly_streak_state_v1_${userId}`);
};

const applyMissedWeek = (streakData: WeeklyStreakState, missedWeekStart: Date): WeeklyStreakState => {
  if (streakData.count <= 0) return streakData;
  const monthKey = getMonthKey(missedWeekStart);
  if (!streakData.protectedMonths?.[monthKey]) {
    return {
      ...streakData,
      protectedMonths: { ...(streakData.protectedMonths || {}), [monthKey]: true },
      lastProtectionUsedAt: missedWeekStart.toISOString(),
      lastPenalty: null
    };
  }
  const nextCount = Math.floor(streakData.count * 0.75);
  return {
    ...streakData,
    count: nextCount,
    lastPenalty: {
      from: streakData.count,
      to: nextCount,
      week: getWeekKey(missedWeekStart)
    }
  };
};

const awardStreakWeek = (streakData: WeeklyStreakState, weekStart: Date): WeeklyStreakState => {
  const weekKey = getWeekKey(weekStart);
  const isNewWeek = !streakData.awardedWeeks?.[weekKey];
  const nextCount = streakData.count + (isNewWeek ? 1 : 0);
  const weekBonus = calculateStreakWeekBonus(nextCount);
  const previousBonus = toNonNegativeNumber(
    streakData.awardedWeeks?.[weekKey]?.bonus
  );

  return {
    ...streakData,
    count: nextCount,
    bestCount: Math.max(Number(streakData.bestCount || 0), nextCount),
    streakBonusTotal: Math.max(
      0,
      toNonNegativeNumber(streakData.streakBonusTotal) - previousBonus + weekBonus
    ),
    awardedWeeks: {
      ...(streakData.awardedWeeks || {}),
      [weekKey]: {
        componentId: getWeeklyStreakExperienceComponentId(weekKey),
        bonus: weekBonus,
        streakWeekNumber: nextCount
      }
    }
  };
};

const getSortedAwardEntries = (awardedWeeks: Record<string, WeeklyStreakAward> = {}) =>
  Object.entries(awardedWeeks || {})
    .sort(([left], [right]) => left.localeCompare(right));

const sumAwardBonuses = (entries: [string, WeeklyStreakAward][] = []) => entries.reduce(
  (total, [, award]) => Math.min(
    Number.MAX_SAFE_INTEGER,
    total + toNonNegativeNumber(award?.bonus)
  ),
  0
);

const getLegacyReplayAnchor = (streakData: WeeklyStreakState) => {
  const entries = getSortedAwardEntries(streakData.awardedWeeks);
  const legacyEntries = entries.filter(([weekKey]) =>
    weekKey < WEEKLY_STREAK_REPLAY_START_WEEK
  );
  const legacyAwardBest = legacyEntries.reduce(
    (best, [, award]) => Math.max(
      best,
      toNonNegativeInteger(award?.streakWeekNumber)
    ),
    0
  );
  const legacyAwardExperience = sumAwardBonuses(legacyEntries);

  if (
    Number(streakData.streakReplayVersion) >= WEEKLY_STREAK_REPLAY_VERSION &&
    streakData.streakReplayBoundaryWeek === WEEKLY_STREAK_REPLAY_START_WEEK &&
    streakData.streakReplayAnchor
  ) {
    // An early rollout could infer the boundary anchor from a malformed
    // current-week entry (#5 instead of #6). The named pre-boundary awards are
    // authoritative: five consecutive entries mean the replay must start at
    // five, regardless of the stale stored anchor.
    const repairedCount = Math.max(
      toNonNegativeInteger(streakData.streakReplayAnchor.count),
      legacyAwardBest
    );
    return {
      anchor: {
        ...streakData.streakReplayAnchor,
        count: repairedCount,
        bestCount: Math.max(
          repairedCount,
          toNonNegativeInteger(streakData.streakReplayAnchor.bestCount),
          legacyAwardBest
        )
      },
      legacyStreakBonusTotal: Math.max(
        toNonNegativeNumber(streakData.legacyStreakBonusTotal),
        legacyAwardExperience
      )
    };
  }

  const replayEntries = entries.filter(([weekKey]) =>
    weekKey >= WEEKLY_STREAK_REPLAY_START_WEEK
  );
  const firstReplayWeekNumber = toNonNegativeInteger(
    replayEntries[0]?.[1]?.streakWeekNumber
  );
  // During rollout an older client may have saved count/aggregate for the
  // boundary week without the amount-bearing map entry. At the boundary there
  // is exactly one reversible week, so its target can still be separated from
  // the legacy baseline without guessing historical weeks.
  const hasUnledgeredBoundaryAward =
    replayEntries.length === 0 &&
    streakData.lastUpdatedWeek === WEEKLY_STREAK_REPLAY_START_WEEK &&
    toNonNegativeInteger(streakData.count) > 0 &&
    toNonNegativeNumber(streakData.streakBonusTotal) >=
      calculateStreakWeekBonus(toNonNegativeInteger(streakData.count));
  const anchorCount = firstReplayWeekNumber > 0
    ? Math.max(legacyAwardBest, firstReplayWeekNumber - 1)
    : hasUnledgeredBoundaryAward
      ? Math.max(
          legacyAwardBest,
          toNonNegativeInteger(streakData.count) - 1
        )
      : Math.max(legacyAwardBest, toNonNegativeInteger(streakData.count));
  const anchorBestCount = replayEntries.length > 0 || hasUnledgeredBoundaryAward
    ? Math.max(legacyAwardBest, anchorCount)
    : Math.max(toNonNegativeInteger(streakData.bestCount), anchorCount);
  const boundaryMonth = WEEKLY_STREAK_REPLAY_START_WEEK.slice(0, 7);
  const anchorProtectedMonths = Object.fromEntries(
    Object.entries(streakData.protectedMonths || {})
      .filter(([monthKey, used]) => used && monthKey < boundaryMonth)
  );
  const replayExperienceAlreadyIncluded = hasUnledgeredBoundaryAward
    ? calculateStreakWeekBonus(toNonNegativeInteger(streakData.count))
    : sumAwardBonuses(replayEntries);
  const storedExperience = toNonNegativeNumber(streakData.streakBonusTotal);
  const legacyStreakBonusTotal = Math.max(
    legacyAwardExperience,
    storedExperience > 0
      ? Math.max(0, storedExperience - replayExperienceAlreadyIncluded)
      : legacyAwardExperience
  );

  return {
    anchor: {
      count: anchorCount,
      bestCount: anchorBestCount,
      protectedMonths: anchorProtectedMonths
    },
    legacyStreakBonusTotal
  };
};

// Rebuild every reversible week from the same immutable boundary anchor. This
// makes edit/delete/re-add symmetric and correctly handles repeated count
// values after a penalty: identity is the week, while bonus is its target.
export const replayWeeklyStreakAwards = (
  sourceStreakData: Partial<WeeklyStreakState>,
  ratings: Rating[] = [],
  referenceDate = new Date()
): {
  streakData: WeeklyStreakState;
  removedWeeks: string[];
} => {
  const streakData = normalizeStreakData(sourceStreakData);
  const currentWeekStart = getWeekStart(referenceDate);
  const currentWeekKey = getWeekKey(currentWeekStart);
  if (currentWeekKey < WEEKLY_STREAK_REPLAY_START_WEEK) {
    return { streakData, removedWeeks: [] };
  }

  const { anchor, legacyStreakBonusTotal } = getLegacyReplayAnchor(streakData);
  const legacyAwardedWeeks = Object.fromEntries(
    getSortedAwardEntries(streakData.awardedWeeks)
      .filter(([weekKey]) => weekKey < WEEKLY_STREAK_REPLAY_START_WEEK)
  );
  const ratingsByWeek = getRatingsByWeek(ratings);
  let count = toNonNegativeInteger(anchor.count);
  let bestCount = Math.max(
    toNonNegativeInteger(anchor.bestCount),
    count
  );
  let protectedMonths = { ...(anchor.protectedMonths || {}) };
  let lastProtectionUsedAt: string | null = null;
  let lastPenalty: StreakPenalty | null = null;
  const replayAwardedWeeks: Record<string, WeeklyStreakAward> = {};
  let weekStart = parseWeekKey(WEEKLY_STREAK_REPLAY_START_WEEK);

  while (weekStart && weekStart <= currentWeekStart) {
    const weekKey = getWeekKey(weekStart);
    if ((ratingsByWeek.get(weekKey) || 0) >= 1) {
      count += 1;
      bestCount = Math.max(bestCount, count);
      replayAwardedWeeks[weekKey] = {
        componentId: getWeeklyStreakExperienceComponentId(weekKey),
        bonus: calculateStreakWeekBonus(count),
        streakWeekNumber: count
      };
    } else if (count > 0 && weekStart < currentWeekStart) {
      const monthKey = getMonthKey(weekStart);
      if (!protectedMonths[monthKey]) {
        protectedMonths = { ...protectedMonths, [monthKey]: true };
        lastProtectionUsedAt = weekStart.toISOString();
        lastPenalty = null;
      } else {
        const nextCount = Math.floor(count * 0.75);
        lastPenalty = {
          from: count,
          to: nextCount,
          week: weekKey
        };
        count = nextCount;
      }
    }
    weekStart = new Date(weekStart.getTime() + WEEK);
  }

  const awardedWeeks = {
    ...legacyAwardedWeeks,
    ...replayAwardedWeeks
  };
  const replayed = {
    ...streakData,
    count,
    bestCount,
    awardedWeeks,
    protectedMonths,
    streakReplayVersion: WEEKLY_STREAK_REPLAY_VERSION,
    streakReplayBoundaryWeek: WEEKLY_STREAK_REPLAY_START_WEEK,
    streakReplayAnchor: anchor,
    legacyStreakBonusTotal,
    lastProtectionUsedAt,
    lastPenalty,
    lastUpdatedWeek: currentWeekKey
  };
  replayed.streakBonusTotal = getWeeklyStreakLedgerExperience(replayed);
  replayed.bonusTotal = replayed.streakBonusTotal + toNonNegativeNumber(
    replayed.qualityBonusTotal
  );

  const removedWeeks = Object.keys(streakData.awardedWeeks || {})
    .filter((weekKey) =>
      weekKey >= WEEKLY_STREAK_REPLAY_START_WEEK &&
      !Object.prototype.hasOwnProperty.call(replayAwardedWeeks, weekKey)
    );

  return { streakData: replayed, removedWeeks };
};

export const updateWeeklyStreak = async (
  userId: string | null | undefined,
  ratings: Rating[],
  firestore: firebase.firestore.Firestore | null | undefined,
  persist = true,
  {
    recalculateQualityWeeks = [],
    cache = !persist
  }: StreakUpdateOptions = {}
) => {
  if (!userId) return { ...defaultStreakData };

  let streakData = await loadStreakData(userId, firestore, { cache });
  const storedSignature = JSON.stringify(streakData);
  const storedProtectedMonths = { ...(streakData.protectedMonths || {}) };
  const currentWeekStart = getWeekStart();
  const currentWeekKey = getWeekKey(currentWeekStart);
  let removedStreakWeeks: string[] = [];

  if (currentWeekKey >= WEEKLY_STREAK_REPLAY_START_WEEK) {
    const replay = replayWeeklyStreakAwards(
      streakData,
      ratings,
      currentWeekStart
    );
    streakData = replay.streakData;
    removedStreakWeeks = replay.removedWeeks;
  } else {
    // Historical behavior remains available for old snapshots/tests, but even
    // here XP changes only when a concrete week target is added.
    const lastUpdatedWeekStart = streakData.lastUpdatedWeek
      ? parseWeekKey(streakData.lastUpdatedWeek)
      : null;
    if (lastUpdatedWeekStart && getWeekKey(lastUpdatedWeekStart) !== currentWeekKey) {
      let weekStart = new Date(lastUpdatedWeekStart.getTime() + WEEK);
      while (weekStart < currentWeekStart) {
        const weekFilms = getFilmsForWeek(ratings, weekStart);
        streakData = weekFilms >= 1
          ? awardStreakWeek(streakData, weekStart)
          : applyMissedWeek(streakData, weekStart);
        weekStart = new Date(weekStart.getTime() + WEEK);
      }
    }

    if (getFilmsThisWeek(ratings) >= 1) {
      streakData = awardStreakWeek(streakData, currentWeekStart);
    }

    const calculatedStreak = calculateCurrentWeeklyStreakFromRatings(
      ratings,
      currentWeekStart
    );
    if (calculatedStreak > Number(streakData.count || 0)) {
      streakData = {
        ...streakData,
        count: calculatedStreak,
        bestCount: Math.max(
          Number(streakData.bestCount || 0),
          calculatedStreak
        )
      };
    }
  }

  const awardedQualityWeeks = Object.keys(streakData.awardedWeeks || {});
  const activeStreakQualityWeeks = getCurrentWeeklyStreakWeekKeysFromRatings(
    ratings,
    currentWeekStart
  );
  // Old versions rebuilt quality from every rating in the account. That made
  // isolated pre-feature ratings look like earned quality weeks. Once the
  // streak ledger exists, quality may only belong to a week recorded by that
  // ledger or to a contiguous active week recoverable from the ratings.
  // Accounts predating the ledger keep the legacy fallback until a real
  // awarded week is recorded, avoiding an accidental loss of earned XP.
  const allowedQualityWeeks = awardedQualityWeeks.length > 0
    ? [...new Set([...awardedQualityWeeks, ...activeStreakQualityWeeks])]
    : null;
  let finalizedWeekQualities = finalizeClosedWeekQualities(
    streakData.finalizedWeekQualities,
    ratings,
    currentWeekStart,
    [
      ...recalculateQualityWeeks,
      ...getInvalidStoredQualityWeeks(ratings)
    ],
    allowedQualityWeeks
  );
  const removedQualityWeeks = Object.keys(streakData.finalizedWeekQualities || {})
    .filter((weekKey) => !Object.prototype.hasOwnProperty.call(finalizedWeekQualities, weekKey));
  const finalizedQualityStats = getSnapshotQualityStats(finalizedWeekQualities);
  const removedProtectionMonths = Object.keys(storedProtectedMonths)
    .filter((monthKey) =>
      !Object.prototype.hasOwnProperty.call(
        streakData.protectedMonths || {},
        monthKey
      )
    );
  const bestCount = Math.max(
    Number(streakData.bestCount || 0),
    Number(streakData.count || 0)
  );
  const streakBonusTotal = getWeeklyStreakLedgerExperience(streakData);
  streakData = {
    ...streakData,
    ...finalizedQualityStats,
    finalizedWeekQualities,
    weekQualities: finalizedWeekQualities,
    weekQualitySnapshotVersion: WEEK_QUALITY_SNAPSHOT_VERSION,
    weekQualityJournalRecoveredAt: streakData.weekQualityJournalRecoveredAt || null,
    bestCount,
    streakBonusTotal,
    bonusTotal: streakBonusTotal + finalizedQualityStats.qualityBonusTotal,
    lastUpdatedWeek: currentWeekKey
  };

  // Reading profile/stats must not rewrite the same state on every mount.
  // Persist only a real weekly transition or progress change.
  if (persist && JSON.stringify(streakData) !== storedSignature) {
    await saveStreakData(userId, firestore, streakData, {
      removedQualityWeeks,
      removedStreakWeeks,
      removedProtectionMonths
    });
  }
  return streakData;
};

export const getWeeklyStreakStats = async (
  userId: string | null | undefined,
  ratings: Rating[],
  firestore: firebase.firestore.Firestore | null | undefined,
  persist = true,
  options: StreakUpdateOptions = {}
) => {
  const streakData = await updateWeeklyStreak(userId, ratings, firestore, persist, options);
  const filmsThisWeek = getFilmsThisWeek(ratings);
  const currentWeekKey = getWeekKey();
  const qualityRatingsByWeek = getQualityRatingsByWeek(ratings);
  const qualityFilmsThisWeek = qualityRatingsByWeek.get(currentWeekKey) || 0;
  const currentQualityRank = getQualityRankForFilms(qualityFilmsThisWeek);
  const currentRank = currentQualityRank || WEEK_QUALITY_RANKS[0];
  const nextRank = WEEK_QUALITY_RANKS.find((rank) => rank.weeklyFilms > qualityFilmsThisWeek) || null;
  const monthKey = getMonthKey();
  const finalizedQualityStats = getSnapshotQualityStats(streakData.finalizedWeekQualities);

  return {
    ...streakData,
    // The current rank is a preview. Only a closed-week snapshot contributes
    // to profile XP, otherwise changing a current rating could revoke XP.
    qualityCounts: finalizedQualityStats.qualityCounts,
    qualityBonusTotal: finalizedQualityStats.qualityBonusTotal,
    weekQualities: { ...(streakData.finalizedWeekQualities || {}) },
    totalQualifiedWeeks: finalizedQualityStats.totalQualifiedWeeks,
    bonusTotal: Number(streakData.streakBonusTotal || 0) + finalizedQualityStats.qualityBonusTotal,
    difficulty: currentRank.key,
    rank: currentRank.key,
    rankName: currentRank.name,
    rankIcon: currentRank.icon,
    rankBonus: currentRank.bonus,
    filmsThisWeek,
    qualityFilmsThisWeek,
    requiredFilms: currentRank.weeklyFilms,
    nextRank,
    protectionAvailable: !streakData.protectedMonths?.[monthKey],
    progressThisWeek: Math.min(
      filmsThisWeek / Math.max(1, nextRank?.weeklyFilms || currentRank.weeklyFilms || 1),
      1
    ),
    isCurrentWeekComplete: filmsThisWeek >= 1
  };
};

export const resetWeeklyStreak = async (userId: string | null | undefined, firestore: firebase.firestore.Firestore | null | undefined) => {
  if (!userId) return null;
  const newStreakData = {
    ...defaultStreakData,
    lastUpdatedWeek: getWeekKey()
  };
  await saveStreakData(userId, firestore, newStreakData);
  return newStreakData;
};
